/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { tagName } from '@ember-decorators/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { isNone, isEmpty } from '@ember/utils';
import { task } from 'ember-concurrency';

import FormValidations from 'cio/utils/form-validations';
import isValidEmail from 'cio/utils/validators/email';
import AccountAddressValidations from 'cio/utils/validations/account-address';

function notProvided(value) {
  return isNone(value) || isEmpty(value.trim());
}

const Validations = {
  ...AccountAddressValidations,
  name(value) {
    return notProvided(value) ? "Please enter your company's name" : true;
  },
  invoiceEmail: {
    dependentKeys: ['invoiceEmailConfirm'],
    validator(value) {
      if (!isEmpty(value) && !isValidEmail(value)) {
        return 'Please enter a valid email address';
      }

      return true;
    },
  },
  legalEmail: {
    dependentKeys: ['legalEmailConfirm'],
    validator(value) {
      if (!isEmpty(value) && !isValidEmail(value)) {
        return 'Please enter a valid email address';
      }

      return true;
    },
  },
  technicalEmail: {
    dependentKeys: ['technicalEmailConfirm'],
    validator(value) {
      if (!isEmpty(value) && !isValidEmail(value)) {
        return 'Please enter a valid email address';
      }

      return true;
    },
  },
  invoiceEmailConfirm: {
    dependentKeys: ['invoiceEmail'],
    validator(value, model) {
      let invoiceEmail = model.get('invoiceEmail');

      if (this.isInvoiceEmailDirty && invoiceEmail && value !== invoiceEmail) {
        return "Your invoice email addresses don't match";
      }

      return true;
    },
  },
  legalEmailConfirm: {
    dependentKeys: ['legalEmail'],
    validator(value, model) {
      let legalEmail = model.get('legalEmail');

      if (this.isLegalEmailDirty && legalEmail && value !== legalEmail) {
        return "Your legal email addresses don't match";
      }

      return true;
    },
  },
  technicalEmailConfirm: {
    dependentKeys: ['technicalEmail'],
    validator(value, model) {
      let technicalEmail = model.get('technicalEmail');

      if (this.isTechnicalEmailDirty && technicalEmail && value !== technicalEmail) {
        return "Your technical email addresses don't match";
      }

      return true;
    },
  },
};

@tagName('')
export default class SettingsAccountForm extends Component {
  @service apiErrors;

  account = null;
  readOnly = false;
  onSubmit() {}
  onCancel() {}
  tempAccountLogoUrl = null;
  _isTechnicalEmailInputVisible = false;
  _countrySelectOptions = [];

  validations = null;

  constructor() {
    super(...arguments);

    this.set('validations', FormValidations.create({ validations: Validations, context: this, modelKey: 'account' }));
  }

  @computed('validations.hasErrors', 'isAccountLogoDirty', 'account.hasDirtyAttributes')
  get isFormInvalid() {
    let { hasErrors } = this.validations;
    let { isAccountLogoDirty } = this;
    let isDirty = this.account.get('hasDirtyAttributes');

    return hasErrors || (!isAccountLogoDirty && !isDirty);
  }

  @computed('isFormInvalid', 'submitTask.isRunning', 'readOnly')
  get submitDisabled() {
    let { isFormInvalid, submitTask, readOnly } = this;

    return isFormInvalid || submitTask.isRunning || readOnly;
  }

  @computed('tempAccountLogoUrl', 'account.logoUrl')
  get isAccountLogoDirty() {
    return this.tempAccountLogoUrl !== this.account.get('logoUrl');
  }

  @computed('account.invoiceEmail')
  get isInvoiceEmailDirty() {
    return Boolean(this.account.changedAttributes().invoiceEmail);
  }

  @computed('account.legalEmail')
  get isLegalEmailDirty() {
    return Boolean(this.account.changedAttributes().legalEmail);
  }

  @computed('account.technicalEmail')
  get isTechnicalEmailDirty() {
    return Boolean(this.account.changedAttributes().technicalEmail);
  }

  submitTask = task(async (event) => {
    event.preventDefault();

    if (this.isAccountLogoDirty) {
      this.account.set('logoUrl', this.tempAccountLogoUrl);
    }

    await this.validations.validateModel();
    await this.onSubmit();
  });

  _setConfirmableEmail(attrName, touchedPropName) {
    let { account } = this;

    if (isEmpty(account.get(attrName).trim()) || account.changedAttributes()[attrName]) {
      this.set(touchedPropName, false);
    }
    this.validations.validateAttribute(attrName);
  }

  _setConfirmableEmailConfirm(attrName, confirmInputTouchedPropName) {
    this.set(confirmInputTouchedPropName, true);
    this.validations.validateAttribute(attrName);
  }

  init() {
    super.init(...arguments);

    this.setProperties({
      _isTechnicalEmailInputVisible: Boolean(this.account.get('technicalEmail')),
      tempAccountLogoUrl: this.account.get('logoUrl'),
    });

    // virtual, form-specific account properties - reset on each component render
    this.account.setProperties({
      invoiceEmailConfirm: null,
      legalEmailConfirm: null,
      technicalEmailConfirm: null,
    });
  }

  @action
  resetTechnicalEmail() {
    this.account.setProperties({ technicalEmail: '', technicalEmailConfirm: null });
    this.setProperties({ _technicalEmailConfirmTouched: false, _isTechnicalEmailInputVisible: false });
  }

  @action
  setInvoiceEmail() {
    this._setConfirmableEmail('invoiceEmail', '_invoiceEmailConfirmTouched');
  }

  @action
  setLegalEmail() {
    this._setConfirmableEmail('legalEmail', '_legalEmailConfirmTouched');
  }

  @action
  setTechnicalEmail() {
    this._setConfirmableEmail('technicalEmail', '_technicalEmailConfirmTouched');
  }

  @action
  setInvoiceEmailConfirm(value) {
    this._setConfirmableEmailConfirm('invoiceEmailConfirm', '_invoiceEmailConfirmTouched', value);
  }

  @action
  setLegalEmailConfirm(value) {
    this._setConfirmableEmailConfirm('legalEmailConfirm', '_legalEmailConfirmTouched', value);
  }

  @action
  setTechnicalEmailConfirm(value) {
    this._setConfirmableEmailConfirm('technicalEmailConfirm', '_technicalEmailConfirmTouched', value);
  }

  @action
  onBillingAddressFormChange(field, value, errors) {
    this.account.set(field, value);
    this.set('_addressErrors', errors);
  }

  @action
  setAddressFormValue(field, value) {
    this.account.set(field, value);
  }

  @action
  onValueChange(field, value) {
    this.account.set(field, value);
    this.validations.validateAttribute(field);
  }
}
